import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Phone, Plus } from 'lucide-react';
import type { Branch } from '@tengesa/shared';
import { api } from '../../lib/api';
import { Badge, Button, Dialog, ErrorNotice, Field, FormError, PageHeader, Panel, Spinner, Switch, TextInput, fieldErrors, useToast } from '../../ui';

export function BranchesPage() {
  const q = useQuery({ queryKey: ['branches'], queryFn: () => api.get<Branch[]>('/api/branches') });
  const [editing, setEditing] = useState<Branch | 'new' | null>(null);
  return (
    <>
      <PageHeader
        title="Branches"
        description="Each shop or outlet. Stock, tills and sales are kept per branch."
        actions={
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>
            Add branch
          </Button>
        }
      />
      {q.isLoading && <Spinner />}
      {q.error && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.data && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {q.data.map((b) => (
            <button key={b.id} onClick={() => setEditing(b)} className="text-left">
              <Panel className="h-full p-5 transition-colors hover:border-ledger-500/50">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-lg font-semibold leading-tight">{b.name}</p>
                    <p className="text-sm text-muted">Code {b.code}</p>
                  </div>
                  {b.isActive ? <Badge tone="ok">Open</Badge> : <Badge>Closed</Badge>}
                </div>
                <div className="mt-4 space-y-1.5 text-sm text-muted">
                  {b.address && (
                    <p className="flex gap-2">
                      <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden />
                      {b.address}
                    </p>
                  )}
                  {b.phone && (
                    <p className="flex gap-2">
                      <Phone className="mt-0.5 size-4 shrink-0" aria-hidden />
                      {b.phone}
                    </p>
                  )}
                </div>
                <p className="mt-4 border-t border-line pt-3 text-sm">
                  {b.userCount} staff {b.userCount === 1 ? 'member' : 'members'} assigned
                </p>
              </Panel>
            </button>
          ))}
        </div>
      )}
      {editing && <BranchDialog branch={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function BranchDialog({ branch, onClose }: { branch: Branch | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(branch?.name ?? '');
  const [code, setCode] = useState(branch?.code ?? '');
  const [address, setAddress] = useState(branch?.address ?? '');
  const [phone, setPhone] = useState(branch?.phone ?? '');
  const [isActive, setActive] = useState(branch?.isActive ?? true);
  const m = useMutation({
    mutationFn: () => {
      const body = { name, code, address, phone, isActive };
      return branch ? api.put<Branch>(`/api/branches/${branch.id}`, body) : api.post<Branch>('/api/branches', body);
    },
    onSuccess: () => {
      toast(branch ? 'Branch saved' : 'Branch added');
      void qc.invalidateQueries({ queryKey: ['branches'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    m.mutate();
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={branch ? branch.name : 'Add branch'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="branch-form" busy={m.isPending}>
            {branch ? 'Save branch' : 'Add branch'}
          </Button>
        </>
      }
    >
      <FormError error={m.error && !Object.keys(f).length ? m.error : null} />
      <form id="branch-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-[1fr_140px]" noValidate>
        <Field label="Branch name" htmlFor="b-name" error={f.name}>
          <TextInput id="b-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus invalid={!!f.name} placeholder="e.g. Highfield" />
        </Field>
        <Field label="Short code" htmlFor="b-code" error={f.code} hint="Printed on receipts">
          <TextInput id="b-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={8} invalid={!!f.code} placeholder="HFD" />
        </Field>
        <Field label="Address" htmlFor="b-addr" error={f.address} className="sm:col-span-2">
          <TextInput id="b-addr" value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        <Field label="Phone" htmlFor="b-phone" error={f.phone} className="sm:col-span-2">
          <TextInput id="b-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+263 242 123 456" invalid={!!f.phone} />
        </Field>
        {branch && (
          <div className="flex items-center justify-between gap-4 rounded-[8px] bg-paper px-4 py-3 sm:col-span-2">
            <div>
              <p className="font-medium">Branch open</p>
              <p className="text-sm text-muted">A closed branch disappears from branch pickers. Its records are kept.</p>
            </div>
            <Switch checked={isActive} onChange={setActive} label="Branch open" />
          </div>
        )}
      </form>
    </Dialog>
  );
}
